<!-- eslint-disable no-console -->
<script setup lang="ts">
import MazPhoneNumberInput from "maz-ui/components/MazPhoneNumberInput";
import { companyApi } from "~/composables/companyApi";
import countries from "~/composables/utils/countries";

const props = defineProps<{
  updateCallback: (data: any) => Promise<void>;
  userId: any;
  companyId: any;
  companyInfo: any;
}>();
const phoneNumber = ref();
const phoneNumberResults = ref();
const customerDetails = ref({
  firstName: "",
  lastName: "",
  companyName: "",
  email: "",
  phone: "",
  streetAddress: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "United States",
  country_code: "",
  leads: "Please Select*",
  notes: "",
});

const leads = ref([]);
const buttonTitle = ref("Submit");
const buttonState = ref(false);
const loading = ref(false);
const success = ref(false);

async function getCompanyLeads() {
  // await new Promise((resolve, reject) => setTimeout(resolve, 2000))
  const res = await companyApi.GET_COMPANY_LEADS({
    company_id: props.companyId,
    user_id: props.userId,
  });
  leads.value = res.data.map((result: any) => {
    return {
      label: result.lead_name,
      value: result.docId,
    };
  });
  return leads.value;
}

async function submitHandle() {
  buttonState.value = false;
  try {
    if (loading.value === true) return;
    if (
      !customerDetails.value.firstName ||
      customerDetails.value.firstName.trim() === "" ||
      customerDetails.value.leads === "Please Select*" ||
      !customerDetails.value.lastName ||
      customerDetails.value.lastName.trim() === "" ||
      !customerDetails.value.email ||
      !customerDetails.value.streetAddress ||
      !customerDetails.value.city ||
      !customerDetails.value.state ||
      !customerDetails.value.postalCode ||
      !customerDetails.value.country
    )
      throw new Error("Please fill out all required fields");

    loading.value = true;
    const res = await companyApi.CREATE_COMPANY_CUSTOMER_FORM({
      customer_email: customerDetails.value.email,
      company_id: props.companyId,
      user_id: props.userId,
      first_name: customerDetails.value.firstName,
      last_name: customerDetails.value.lastName,
      company_name: customerDetails.value.companyName,
      address: {
        city: customerDetails.value.city,
        state: customerDetails.value.state,
        street_address: customerDetails.value.streetAddress,
        address2: customerDetails.value.address2,
        zip_code: customerDetails.value.postalCode,
        country: customerDetails.value.country,
      },
      country_code: Number.parseInt(phoneNumberResults.value.countryCallingCode),
      country_name: phoneNumberResults.value.countryCode,
      phone_number: phoneNumberResults.value.formatNational,
      lead_id: customerDetails.value.leads,
      notes: customerDetails.value.notes,
    });
    // console.log(res.statusCode);
    // console.log(res);
    if (res === undefined) {
      throw new Error("Error occurred");
    } else if (res.statusCode === 500) {
      ElNotification({
        title: "Error 500 occurred!",
        message: "Please try again later.",
        type: "error",
      });
      buttonState.value = true;
    } else {
      ElNotification({
        title: "Success",
        message: "Form submitted successfully.",
        type: "success",
      });
      buttonTitle.value = "Success";
      success.value = true;
      customerDetails.value = {
        firstName: "",
        lastName: "",
        companyName: "",
        email: "",
        phone: "",
        streetAddress: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "United States",
        country_code: "",
        leads: "Please Select*",
        notes: "",
      };
    }
  } catch (error) {
    console.log("error", error);
    ElNotification({
      title: "Error occurred!",
      message: "Please check all your fields and try again.",
      type: "error",
    });
    buttonState.value = true;
  } finally {
    loading.value = false;
  }
}

function restartForm() {
  success.value = false;
  phoneNumber.value = "";
  buttonTitle.value = "Submit";
  customerDetails.value = {
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    phone: "",
    streetAddress: "",
    address2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "United States",
    country_code: "",
    leads: "Please Select*",
    notes: "",
  };
}
</script>

<template>
  <div class="bg-gray-100">
    <div class="mx-auto max-w-7xl px-2 py-0 sm:py-0 lg:px-8">
      <div class="relative bg-white shadow-xl">
        <h2 class="sr-only">Contact us</h2>

        <div v-if="success" class="success-message">
          <div class="success-icon">
            <!-- Add your green circle with a checkbox here -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <p>Form submitted successfully!</p>
          <button @click="restartForm" class="restart-button">Restart Form</button>
        </div>
        <div v-if="!success" class="grid grid-cols-1">
          <!-- Contact information -->

          <!-- Contact form -->
          <FormKit
            v-model="customerDetails"
            type="form"
            @submit="submitHandle"
            :config="{ validationVisibility: 'true' }"
          >
            <div class="px-6 py-10 sm:px-10 lg:col-span-2 xl:p-12 text-left">
              <h3 class="text-lg font-medium text-gray-900">Contact Us</h3>
              <div
                class="sm:w3/4 md:w-1/2 w-full max-w-full grid md:grid-cols-2 grid-cols-1 md:gap-5"
              >
                <div class="max-w-full">
                  <FormKit
                    name="firstName"
                    type="text"
                    label="First Name"
                    :value="customerDetails.firstName"
                    placeholder="First Name"
                    validation="required|alpha"
                    :validation-visibility="false ? 'live' : 'blur'"
                    max-w-full
                  />
                </div>
                <div class="max-w-full">
                  <FormKit
                    name="lastName"
                    type="text"
                    label="Your Last Name"
                    :value="customerDetails.lastName"
                    placeholder="Last Name"
                    validation="required|alpha"
                    max-w-full
                  />
                </div>
              </div>
              <div class="max-w-full mt-1">
                <FormKit
                  name="companyName"
                  type="text"
                  label="Company Name (Optional)"
                  :value="customerDetails.companyName"
                  placeholder="Enter company name"
                  max-w-full
                />
              </div>
              <div
                class="md:flex md:flex-row md:gap-8 justify-start sm:flex-col sm:col sm:gap-0"
              >
                <div class="w-full max-w-full">
                  <label
                    for="phone_number"
                    class="block text-sm font-medium text-gray-900"
                    >Phone Number</label
                  >
                  <div class="mt-1 w-full max-w-full">
                    <MazPhoneNumberInput
                      v-model="phoneNumber"
                      show-code-on-list
                      color="info"
                      :preferred-countries="['US']"
                      :success="phoneNumberResults?.isValid"
                      max-w-md
                      bg-white
                      rounded
                      mb-4
                      z-36
                      @update="phoneNumberResults = $event"
                    />
                  </div>
                </div>
              </div>

              <div class="mt-1 w-full max-w-full">
                <FormKit
                  type="email"
                  name="email"
                  label="Email Address"
                  validation="required|email"
                  validation-visibility="blur"
                  placeholder="adam@quoteiq.io"
                  :value="customerDetails.email"
                  bg-white
                />
              </div>

              <!--- NEW ADDRESS --->
              <div
                class="sm:w3/4 md:w-1/2 w-full max-w-full grid md:grid-cols-2 grid-cols-1 md:gap-5"
              >
                <div class="mt-1 w-full max-w-full">
                  <FormKit
                    name="streetAddress"
                    type="text"
                    label="Street Address"
                    :value="customerDetails.streetAddress"
                    placeholder="Enter street address"
                    validation="required"
                    max-w-full
                  />
                </div>
                <div class="mt-1 w-full max-w-full">
                  <FormKit
                    name="address2"
                    type="text"
                    label="Address Line 2"
                    :value="customerDetails.address2"
                    placeholder="Enter address line 2"
                    max-w-full
                  />
                </div>
              </div>
              <div
                class="sm:w3/4 md:w-1/2 w-full max-w-full grid md:grid-cols-2 grid-cols-1 md:gap-5"
              >
                <div>
                  <FormKit
                    name="city"
                    type="text"
                    label="City"
                    :value="customerDetails.city"
                    placeholder="Enter city"
                    validation="required"
                    max-w-full
                  />
                </div>
                <div>
                  <FormKit
                    name="state"
                    type="text"
                    label="State / Province / Region"
                    :value="customerDetails.state"
                    placeholder="Enter state/province/region"
                    validation="required"
                    max-w-full
                  />
                </div>
              </div>
              <div
                class="sm:w3/4 md:w-1/2 w-full max-w-full grid md:grid-cols-2 grid-cols-1 md:gap-5"
              >
                <div>
                  <FormKit
                    name="postalCode"
                    type="text"
                    label="Postal / Zip Code"
                    :value="customerDetails.postalCode"
                    placeholder="Enter postal/zip code"
                    validation="required"
                    max-w-full
                  />
                </div>
                <div>
                  <FormKit
                    type="autocomplete"
                    name="country"
                    label="Country"
                    placeholder="Example: United States"
                    :options="countries"
                    :value="customerDetails.country"
                    :classes="{
                      inner: {
                        'h-40px': true,
                      },
                    }"
                    max-w-full
                    class="h-xl max-h-xl"
                  />
                  <!-- Dropdown options here -->
                </div>
              </div>

              <div
                class="md:flex md:flex-row md:gap-8 justify-start sm:flex-col sm:col sm:gap-0"
              >
                <!-- <div class="mt-1 w-full max-w-full">
                  <FormKit
                    name="address"
                    type="autocomplete"
                    label="Property Address"
                    validation="required"
                    select-icon="caretDown"
                    :value="customerDetails.address"
                    placeholder="Example: 123 Street Ave, City, FL, USA"
                    :options="searchAddress"
                    class="box-border"
                    open-on-click
                  />
                </div> -->
                <div class="mt-1 w-full max-w-full">
                  <FormKit
                    type="dropdown"
                    name="leads"
                    label="How did you hear about us?"
                    placeholder="Please Select*"
                    :options="getCompanyLeads"
                    class="h-xl max-h-xl"
                    validation="required|alphanumeric"
                    validation-visibility="blur"
                    :validation-messages="{
                      alphanumeric: 'Please select an option',
                    }"
                    style="
                      line-height: 1rem !important;
                      margin-left: 1rem;
                      z-index: 1 !important;
                    "
                  >
                    <!-- OPTION SLOT -->
                    <template #option="{ option }">
                      <div
                        style="
                          line-height: 2rem !important;
                          font-size: 16px;
                          z-index: 1 !important;
                        "
                      >
                        <span>
                          {{ option.label }}
                        </span>
                      </div>
                    </template>
                    <!---->
                  </FormKit>
                </div>
              </div>
              <FormKit
                type="textarea"
                label="Services Requested:"
                name="notes"
                rows="10"
                placeholder=""
                :value="customerDetails.notes"
                px-4
                mx-5
              />
              <div class="items-center flex w-full mx-2 mb-2">
                <input type="checkbox" :checked="true" class="checkbox checkbox-sm" />
                <label pl-2 pr-2 text-0.7rem
                  >I want to receive job updates, and offers sent to my phone
                  number.</label
                >
              </div>
              <!-- <pre wrap> {{ customerDetails }} </pre> -->
              <div class="sm:col-span-2 sm:flex sm:justify-start">
                <!-- <button type="submit" class="mt-2 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto">
                  Submit
                </button> -->
                <CommonButtonWide
                  :on-click="null"
                  :title="buttonTitle"
                  loading-title="Processing"
                  :loading="loading"
                  :error="buttonState"
                  error-message="Error occurred"
                />
              </div>
            </div>
          </FormKit>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.formkit-input {
  height: 40px !important;
}
.success-message {
  display: flex;
  flex-direction: column; /* Make it a column flex container */
  align-items: center;
  justify-content: center;
  animation: fadeIn 1s ease-in;
  font-size: 1.5rem;
  color: #333;
}

.success-icon {
  width: 50px;
  height: 50px;
  margin-bottom: 15px;
  animation: bounce 1s ease-in 1; /* Make it bounce only once */
}

.restart-button {
  margin-top: 15px;
  padding: 10px 20px;
  border: none;
  background-color: #333;
  color: #fff;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-bottom: 15px;
}

.restart-button:hover {
  background-color: #555;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

button {
  margin-top: 1rem;
  display: inline-block;
  background-color: #007bff;
  color: white;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  text-decoration: none;
  font-size: 16px;
  transition: background-color 0.3s ease;
}

button:hover {
  background-color: #0056b3;
}
</style>
